import React, { useCallback } from 'react'
import Link from 'components/Link'
import PropTypes from 'prop-types'
import animationUtils from 'utils/animation'
import VideoPlayer from 'components/VideoPlayer'
import { useComponentId, useFieldId } from 'utils/optimizely'
import trackGTM from 'utils/trackGTM'
import {
  CarouselContent,
  CarouselContentWrapper,
  CarouselCopy,
  CarouselEyebrow,
  CarouselImage,
  CarouselImg,
  CarouselMetaDivider,
  CarouselVideo,
  CarouselOverlayContainer,
} from './CarouselSlide.style'

const CarouselSlide = (props) => {
  const {
    altText,
    currentSlide,
    eyebrow,
    heading,
    index,
    landscape,
    onClick,
    portrait,
    publishDate,
    rtl,
    url,
    video,
    elementId,
  } = props
  const delayObj = animationUtils.calculateDelay({
    eyebrow,
    heading,
  })
  const isActive = currentSlide === index
  const isNext = index === currentSlide + 1
  const isPrev = index === currentSlide - 1
  const showOverlay = !!((heading || eyebrow) && portrait && landscape)
  const showCopy = !!(isActive && heading)
  const hasVideo = video && video.portrait && video.landscape
  const elId = elementId || useComponentId(`Comp_Carousel-00-slide`)

  const OnSlideClick = useCallback(() => {
    if (heading) {
      trackGTM('carousel', 'carousel', 'Carousel_Full_Bleed', heading)
    }
  }, [])

  const slide = (
    <>
      <CarouselOverlayContainer showOverlay={showOverlay}>
        <CarouselImage
          currentSlide={currentSlide}
          onClick={OnSlideClick}
          isNext={isNext}
          isPrev={isPrev}
          rtl={rtl}
          id={useFieldId(elId, 'image-container')}>
          {hasVideo ? (
            <CarouselVideo>
              <VideoPlayer
                id={useFieldId(elId, 'video')}
                name={video.name}
                autoplay={isActive}
                carouselActive={isActive}
                controls={false}
                image={{ landscape, portrait, altText }}
                loop
                muted
                video={video}
                inCarousel
              />
            </CarouselVideo>
          ) : (
            <>
              {portrait && (
                <CarouselImg
                  id={useFieldId(elId, 'portrait-image')}
                  image={portrait.gatsbyImageData}
                  display={['block', null, 'none']}
                  alt={altText}
                />
              )}
              {landscape && (
                <CarouselImg
                  id={useFieldId(elId, 'landscape-image')}
                  image={landscape.gatsbyImageData}
                  display={['none', null, 'block']}
                  alt={altText}
                />
              )}
            </>
          )}
        </CarouselImage>
      </CarouselOverlayContainer>

      <CarouselContent>
        <CarouselContentWrapper id={useFieldId(elId, 'content-container')}>
          {isActive && eyebrow && (
            <CarouselEyebrow
              delay={delayObj.eyebrow}
              id={useFieldId(elId, 'eyebrow')}>
              {eyebrow}
              {eyebrow && publishDate && (
                <CarouselMetaDivider>⬩</CarouselMetaDivider>
              )}
              {publishDate}
            </CarouselEyebrow>
          )}
          {showCopy && (
            <CarouselCopy
              delay={delayObj.heading}
              id={useFieldId(elId, 'copy')}>
              {heading}
            </CarouselCopy>
          )}
        </CarouselContentWrapper>
      </CarouselContent>
    </>
  )

  return url ? (
    <Link to={url} onClick={onClick} id={useFieldId(elId, 'link')}>
      {slide}
    </Link>
  ) : (
    slide
  )
}

CarouselSlide.propTypes = {
  elementId: PropTypes.string,
  altText: PropTypes.string,
  currentSlide: PropTypes.number.isRequired,
  eyebrow: PropTypes.string,
  heading: PropTypes.string,
  index: PropTypes.number.isRequired,
  rtl: PropTypes.bool,
  url: PropTypes.string,
  landscape: PropTypes.shape({
    gatsbyImageData: PropTypes.shape({
      aspectRatio: PropTypes.number.isRequired,
      sizes: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
      srcSet: PropTypes.string.isRequired,
      srcSetWebp: PropTypes.string.isRequired,
    }).isRequired,
  }),
  onClick: PropTypes.func,
  portrait: PropTypes.shape({
    gatsbyImageData: PropTypes.shape({
      aspectRatio: PropTypes.number.isRequired,
      sizes: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
      srcSet: PropTypes.string.isRequired,
      srcSetWebp: PropTypes.string.isRequired,
    }).isRequired,
  }),
  publishDate: PropTypes.string,
  video: PropTypes.shape({
    landscape: PropTypes.string,
    portrait: PropTypes.string,
    opacity: PropTypes.number,
    name: PropTypes.string,
  }),
}

CarouselSlide.defaultProps = {
  elementId: null,
  altText: '',
  eyebrow: '',
  heading: '',
  landscape: null,
  onClick: () => {},
  portrait: null,
  publishDate: '',
  rtl: false,
  url: '',
  video: null,
}

export default CarouselSlide
